// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Shared react-query hook for the pipeline-scoped annotated DOCX blob.
 *
 * Both the visible DocxView (inline tab + modal) and the headless
 * useDocxPageMap import this so they hit one cached fetch instead of
 * each round-tripping to S3 independently.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import type { NodeApiClient } from '@/app-shell/services'

import { getPipelineRedlineDocxUrl } from '../utils/pdf-actions'

export function useAnnotatedDocxBlob(
  apiClient: NodeApiClient,
  orgId: string,
  workspaceId: string,
  documentId: string,
  pipelineId: string,
  version: number,
): UseQueryResult<Blob> {
  return useQuery<Blob>({
    queryKey: ['annotated-docx', orgId, workspaceId, documentId, pipelineId, version],
    enabled: Boolean(documentId && pipelineId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    queryFn: async () => {
      const { downloadUrl } = await getPipelineRedlineDocxUrl(
        apiClient, orgId, workspaceId, documentId, pipelineId, version,
      )
      const res = await fetch(downloadUrl)
      if (!res.ok) throw new Error(`Failed to fetch annotated DOCX (HTTP ${res.status})`)
      return res.blob()
    },
  })
}
